import type { LeaveListItem } from '@lms/shared-types'
import type { PeakPeriod } from '../api/peak-periods'

/**
 * Helpers for assembling a `RecommendPeakConflictInput` from the data
 * already on the manager queue.
 *
 * The server route needs the colliding leave pair AND the peak-period
 * UUID. `LeaveListItem` doesn't carry `peakPeriodId` today (the queue
 * endpoint hasn't been extended), so the FE intersects the leave date
 * range with the active-peak-periods list and picks the first match.
 * Two same-window peak periods is a configuration edge that has never
 * shipped — first-match is good enough until it does.
 */

export interface ConflictPair {
  a: LeaveListItem
  b: LeaveListItem
}

/**
 * Walk the visible pending list and return every unordered pair that
 * overlaps on dates. Mirrors the overlap rule used by `conflictMap` in
 * `ManagerRequestsPage` so the trigger button never offers a pair the
 * banner didn't count.
 */
export function detectConflictPairs(
  pending: ReadonlyArray<LeaveListItem>,
): ReadonlyArray<ConflictPair> {
  const pairs: ConflictPair[] = []
  for (let i = 0; i < pending.length; i += 1) {
    for (let j = i + 1; j < pending.length; j += 1) {
      const a = pending[i]
      const b = pending[j]
      if (!a || !b) continue
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        pairs.push({ a, b })
      }
    }
  }
  return pairs
}

/**
 * Pick the pair to ask ILMU about inside one conflict cluster. The cluster
 * members arrive priority-sorted (highest first), so the first overlapping
 * cross-employee pair is the two top contenders for the contested window.
 *
 * Same-employee rows are skipped (the recommendation scores two *different*
 * people, and the server rejects a same-employee pair with 422). Returns
 * null when no askable pair exists — e.g. a cluster that is one employee's
 * two overlapping requests — in which case the cluster shows no trigger.
 */
export function pickClusterPair(
  cluster: ReadonlyArray<LeaveListItem>,
): ConflictPair | null {
  for (let i = 0; i < cluster.length; i += 1) {
    for (let j = i + 1; j < cluster.length; j += 1) {
      const a = cluster[i]
      const b = cluster[j]
      if (!a || !b) continue
      if (a.employee.id === b.employee.id) continue
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        return { a, b }
      }
    }
  }
  return null
}

/**
 * Return the first active peak period whose window contains a day that
 * BOTH leaves cover. The intersection logic is inclusive on both ends
 * (matches the server's `DateRange` semantics — see
 * `services/api/src/domain/peak_period_scoring.py`).
 *
 * Returns `null` when no peak window covers the collision — in that
 * case the surface should not offer an "ask ILMU" trigger, because the
 * server route will reject the call with 404 / "no peak period covers
 * this collision".
 */
export function pickPeakPeriodForPair(
  pair: ConflictPair,
  periods: ReadonlyArray<PeakPeriod>,
): PeakPeriod | null {
  // Active windows only — inactive periods are HR archival noise.
  const candidates = periods.filter((p) => p.active)
  // Collision window — the dates BOTH leaves cover.
  const overlapStart = pair.a.startDate > pair.b.startDate ? pair.a.startDate : pair.b.startDate
  const overlapEnd = pair.a.endDate < pair.b.endDate ? pair.a.endDate : pair.b.endDate
  if (overlapStart > overlapEnd) return null
  return (
    candidates.find(
      (p) => p.startDate <= overlapEnd && p.endDate >= overlapStart,
    ) ?? null
  )
}

/**
 * Render label for the PeakConflictPanel — e.g. "Aisyah · Jun 25–26".
 * Mirrors the human-readable format used in Slack DMs so the panel and
 * the Slack template line up visually.
 */
export function leaveLabel(leave: LeaveListItem): string {
  const firstName = pickFirstName(leave.employee)
  const range = formatDateRange(leave.startDate, leave.endDate)
  return `${firstName} · ${range}`
}

function pickFirstName(emp: { preferredName: string | null; name: string }): string {
  const source = emp.preferredName ?? emp.name
  const first = source.trim().split(/\s+/)[0]
  return first || source
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

function formatDateRange(startIso: string, endIso: string): string {
  const start = parseIsoDate(startIso)
  const end = parseIsoDate(endIso)
  if (!start || !end) return `${startIso} – ${endIso}`
  const startLabel = `${SHORT_MONTHS[start.month]} ${start.day}`
  if (
    start.year === end.year &&
    start.month === end.month &&
    start.day === end.day
  ) {
    return startLabel
  }
  if (start.year === end.year && start.month === end.month) {
    return `${startLabel}–${end.day}`
  }
  const endLabel = `${SHORT_MONTHS[end.month]} ${end.day}`
  return `${startLabel} – ${endLabel}`
}

interface ParsedDate {
  year: number
  month: number
  day: number
}

function parseIsoDate(iso: string): ParsedDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
  }
}
